/**
 * Backend client — optional by construction.
 *
 * The game must be fully playable with no server at all (share links carry the
 * whole room), so every call here degrades to `null`/`false` and the caller
 * falls back to localStorage. `available()` probes once and caches, which also
 * means a CrazyGames build pointed at a dead origin simply behaves like the
 * offline slice instead of hanging on a spinner.
 */

import { decodeRoom, encodeRoom } from '../core/codec'
import type { RoomDef } from '../core/room'
import type { Replay } from '../sim/replay'

const TIMEOUT_MS = 5000

export interface RoomSummary {
  id: string
  name: string
  ownerName: string
  taunt: string
  raids: number
  escapes: number
  room: RoomDef | null
}

interface RoomRowJson {
  id: string
  code: string
  name: string
  owner_name: string
  taunt: string
  raids: number
  escapes: number
}

async function request(path: string, init?: RequestInit): Promise<Response | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const res = await fetch(path, { ...init, signal: controller.signal })
    return res.ok ? res : null
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

function toSummary(row: RoomRowJson): RoomSummary {
  return {
    id: row.id,
    name: row.name,
    ownerName: row.owner_name,
    taunt: row.taunt,
    raids: row.raids,
    escapes: row.escapes,
    room: decodeRoom(row.code),
  }
}

class Api {
  private probe: Promise<boolean> | null = null

  /**
   * A 200 is not enough. A Vite dev server (and any SPA fallback) answers
   * `/api/health` with index.html, which would make the client believe there is
   * a backend and then bury the console in 404s. So the probe insists on the
   * actual JSON contract.
   */
  available(): Promise<boolean> {
    if (!this.probe) {
      this.probe = request('/api/health').then(async (res) => {
        if (!res) return false
        const ct = res.headers.get('content-type') ?? ''
        if (!ct.includes('application/json')) return false
        const body = (await res.json().catch(() => null)) as { ok?: unknown } | null
        return body?.ok === true
      })
    }
    return this.probe
  }

  async publishRoom(room: RoomDef): Promise<boolean> {
    if (!(await this.available())) return false
    const res = await request('/api/rooms', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code: encodeRoom(room) }),
    })
    return res !== null
  }

  async fetchRoom(id: string): Promise<RoomSummary | null> {
    if (!(await this.available())) return null
    const res = await request(`/api/rooms/${encodeURIComponent(id)}`)
    if (!res) return null
    const body = (await res.json().catch(() => null)) as { room?: RoomRowJson } | null
    return body?.room ? toSummary(body.room) : null
  }

  /** Recently updated rooms — the "闯一间陌生屋子" pool. */
  async listRooms(limit = 12): Promise<RoomSummary[]> {
    if (!(await this.available())) return []
    const res = await request(`/api/rooms?limit=${limit}`)
    if (!res) return []
    const body = (await res.json().catch(() => null)) as { rooms?: RoomRowJson[] } | null
    return (body?.rooms ?? []).map(toSummary)
  }

  /** Send a reaction back to the room's owner. Fire-and-forget by design. */
  async submitReplay(roomId: string, replay: Replay): Promise<boolean> {
    if (!(await this.available())) return false
    const res = await request(`/api/rooms/${encodeURIComponent(roomId)}/replays`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(replay),
    })
    return res !== null
  }

  async fetchReplays(roomId: string, since = 0): Promise<Replay[]> {
    if (!(await this.available())) return []
    const res = await request(
      `/api/rooms/${encodeURIComponent(roomId)}/replays?since=${Math.floor(since)}`,
    )
    if (!res) return []
    const body = (await res.json().catch(() => null)) as { replays?: Replay[] } | null
    return body?.replays ?? []
  }
}

export const api = new Api()
